/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file sportSlice.js
 * @description Redux slice for managing sport related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the sport slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (sports = null) => ({
  error: null,
  loading: false,
  sports,
});

/**
 * @function getSports
 * @description Async thunk for get sports operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getSports = createAsyncThunk("sports/loadAll", async ({ cancelToken, lineId, originId }, thunkAPI) => {
  try {
    const { authToken, language } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/sports?originId=${originId}&lineId=${lineId}`, {
      cancelToken,
    });

    const map = {};
    result.data.forEach((sport) => {
      map[sport.code] = sport;
    });

    return {
      sports: map,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain the sports tree", // serializable (err.response.data)
      name: "Sports Tree Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant sportSlice
 * @description Redux slice for sport management
 */
const sportSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getSports.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getSports.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getSports.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        state.sports = action.payload.sports;
      });
  },
  initialState: getInitialState(),
  name: "sport",
  reducers: {},
});
const { reducer } = sportSlice;
export default reducer;
